import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getHsUid, getHsName } from "./hsAuth";
import { listMySchools } from "./happyschoolApi";
import { getActiveSchoolId, setActiveSchoolId } from "./activeSchool";
import HappySchoolJoin from "./HappySchoolJoin";
import HappySchoolStudentPortal from "./HappySchoolStudentPortal";
import HappySchoolStaffPortal from "./HappySchoolStaffPortal";
import HappySchoolTeachingHub from "./HappySchoolTeachingHub";

const HappySchoolHome = () => {
  const [uid, setUid] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeSchoolId, setActive] = useState(getActiveSchoolId());

  // Đăng nhập KHÔNG còn qua Firebase Auth — trạng thái đăng nhập đọc thẳng từ
  // token riêng lưu trong localStorage (xem hsAuth.js), không cần lắng nghe
  // theo kiểu onAuthStateChanged (đồng bộ, không có độ trễ).
  useEffect(() => {
    setUid(getHsUid());
    setDisplayName(getHsName());
    setAuthChecked(true);
  }, []);

  const loadSchools = () => {
    setLoading(true);
    return listMySchools()
      .then((list) => {
        setSchools(list);
        if (list.length > 0 && (!activeSchoolId || !list.find((s) => s.id === activeSchoolId))) {
          setActiveSchoolId(list[0].id);
          setActive(list[0].id);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!uid) { setSchools([]); return; }
    loadSchools();
    // eslint-disable-next-line
  }, [uid]);

  const activeSchool = schools.find((s) => s.id === activeSchoolId);
  const role = activeSchool ? activeSchool.role : null; // vai trò CHÍNH (cao nhất)
  // Nhiều vai trò: hiện thêm phần của các vai trò phụ (vd Quản trị + Giáo viên -> menu quản trị + cổng giáo viên).
  const roles = activeSchool ? (Array.isArray(activeSchool.roles) && activeSchool.roles.length ? activeSchool.roles : role ? [role] : []) : [];
  const alsoTeacher = roles.some((r) => ["teacher", "schedule_viewer", "staff"].includes(r));

  const menuItems = [
    { to: "/happyschool/schools", label: "Trường của tôi", icon: "🏫", description: "Tạo trường mới hoặc chọn trường đang làm việc.", show: true },
    { to: "/happyschool/my-profile", label: "Thông tin của tôi", icon: "👤", description: "Xem và sửa thông tin cá nhân của bạn (chỉ sửa được của mình).", show: !!activeSchoolId },
    { to: "/happyschool/dashboard", label: "Bảng điều hành (Hiệu trưởng)", icon: "📊", description: "Tổng quan số liệu toàn trường: học sinh, lớp, nhân sự, giáo viên.", show: !!activeSchoolId },
    { to: "/happyschool/staff", label: "Nhân sự toàn trường", icon: "🗂️", description: "Toàn bộ CBCNV mọi Khối (giáo viên, đào tạo, hành chính, bảo vệ...).", show: !!activeSchoolId },
    { to: "/happyschool/homeroom-teachers", label: "Giáo viên chủ nhiệm", icon: "🧑‍🎓", description: "GV chủ nhiệm, SĐT, lớp chủ nhiệm, SĐT lớp trưởng/lớp phó.", show: !!activeSchoolId && ["owner", "admin", "teacher", "staff", "schedule_viewer", "cntt"].includes(role) },
    { to: "/happyschool/directory", label: "Nhân viên toàn trường", icon: "📇", description: "Danh bạ Họ tên + SĐT toàn bộ nhân viên, tìm nhanh theo tên hoặc số điện thoại.", show: !!activeSchoolId },
    { to: "/happyschool/teaching", label: "Quản lý dạy học", icon: "🧑‍🏫", description: "Danh mục nền cho giảng dạy: Giáo viên, Môn học, Lớp học, Tổ hợp bộ môn.", show: !!activeSchoolId },
    { to: "/happyschool/student-lists", label: "Quản lý học sinh", icon: "📁", description: "Học sinh đang học và các danh sách HS: tốt nghiệp, ưu tú, chính sách, sức khỏe, đồng phục, quản sinh...", show: !!activeSchoolId },
    { to: "/happyschool/thi-khao-sat", label: "Thi khảo sát — Khối 12", icon: "📝", description: "Chia lớp 12 theo 3 nhóm môn thi tốt nghiệp, xếp lịch thi, phòng thi, GV coi thi, nhập điểm và thống kê.", show: !!activeSchoolId && roles.some((r) => ["owner", "admin", "teacher"].includes(r)) },
    { to: "/happyschool/generate", label: "Xếp thời khoá biểu", icon: "🗓️", description: "Phân công chuyên môn, phân bổ văn hoá-nghề, giờ học, lịch bận GV, và chạy xếp lịch.", show: !!activeSchoolId },
    { to: "/happyschool/substitute", label: "Giáo viên dạy thay", icon: "☎️", description: "Chọn GV bận/vắng, lớp và tiết — tìm thầy cô cùng bộ môn, cùng khối đang trống tiết để nhờ dạy thay.", show: !!activeSchoolId && (role === "owner" || role === "admin") },
    { to: "/happyschool/chamcong-thang", label: "Chấm công tháng — GV Văn hoá", icon: "🧾", description: "Bảng chấm công gộp theo tháng cho GV Văn hoá: nhập/sửa/xuất Excel, công bố cho GV tự xem.", show: !!activeSchoolId },
    { to: "/happyschool/luong-xe", label: "Lương xe", icon: "🚌", description: "Bảng lương xe đưa đón theo tháng (mỗi sheet 1 tab): sửa như Excel, có công thức, nhập/xuất Excel.", show: !!activeSchoolId },
    { to: "/happyschool/vipham-gv", label: "Vi phạm — GV Văn hoá", icon: "⚠️", description: "Ghi nhận vi phạm và số tiền trừ của GV Văn hoá, công bố cho GV tự xem.", show: !!activeSchoolId },
    { to: "/happyschool/chamcong-dao-tao", label: "Chấm công phòng Đào tạo", icon: "🗓️", description: "Chấm công theo ngày công cho nhân viên phòng Đào tạo, nhập/sửa/xuất Excel.", show: !!activeSchoolId },
    { to: "/happyschool/virtual-classes", label: "Lớp học nghề (lớp ảo)", icon: "🛠️", description: "Lớp ảo gộp HS nhiều lớp (vd 12A9HN) — HS tự đăng ký, không đụng lớp gốc, quản lý danh sách/xuất Excel.", show: !!activeSchoolId },
    { to: "/happyschool/bus", label: "Ô tô đưa đón", icon: "🚌", description: "Quản lý tuyến xe đưa đón: biển số, tài xế, điểm đón, phí.", show: !!activeSchoolId },
    { to: "/happyschool/meals", label: "Ăn bán trú", icon: "🍚", description: "Quản lý suất ăn bán trú theo lớp: loại suất, đơn giá.", show: !!activeSchoolId },
    { to: "/happyschool/parent-companion", label: "Cha mẹ đồng hành cùng học sinh", icon: "👪", description: "Đăng ký phụ huynh đồng hành cùng học sinh: hình thức, thời gian.", show: !!activeSchoolId },
    { to: "/happyschool/admission10", label: "Tuyển sinh học sinh khối 10", icon: "🎓", description: "Danh sách hồ sơ tuyển sinh vào lớp 10: điểm, nguyện vọng, trạng thái.", show: !!activeSchoolId },
    { to: "/happyschool/recruitment", label: "Tuyển dụng", icon: "💼", description: "Quản lý hồ sơ ứng viên tuyển dụng: vị trí, trình độ, trạng thái.", show: !!activeSchoolId },
    { to: "/happyschool/teacher-eval", label: "Đánh giá giáo viên", icon: "⭐", description: "Bảng đánh giá giáo viên theo kỳ: chuyên môn, tinh thần, xếp loại.", show: !!activeSchoolId },
    { to: "/happyschool/van-thu", label: "Văn thư lưu trữ", icon: "🗄️", description: "Danh sách link tài liệu lưu trữ của trường (Google Sheets, Docs...): tiêu đề, tác giả, ngày.", show: !!activeSchoolId },
    { to: "/happyschool/feature-requests", label: "Yêu cầu chức năng", icon: "💡", description: "Định nghĩa chức năng mới: tên, mô tả, file Excel mẫu — gửi lên để đội phát triển làm.", show: !!activeSchoolId && ["owner", "admin", "teacher", "staff"].includes(role) },
    { to: "/happyschool/tuition", label: "Thu học phí", icon: "💰", description: "Thu học phí theo đợt: danh sách lớp, GV chủ nhiệm, ngày thu, tiền mặt, chuyển khoản.", show: !!activeSchoolId && (role === "owner" || role === "admin") },
    { to: "/happyschool/payroll", label: "Thanh toán lương", icon: "💵", description: "Thanh toán lương theo đợt: họ tên, số tiền, số tài khoản, ngân hàng, nội dung chuyển khoản.", show: !!activeSchoolId && (role === "owner" || role === "admin") },
    { to: "/happyschool/my-payroll", label: "Lương của tôi", icon: "🧾", description: "Xem lương khớp đúng tên hồ sơ của bạn. Owner/Admin có thể chọn xem thay 1 người khác.", show: !!activeSchoolId && (role === "owner" || role === "admin") },
    { to: "/happyschool/join-requests", label: "Chờ xác thực", icon: "⏳", description: "Duyệt người tham gia trường chưa khớp hồ sơ (Họ tên + CCCD).", show: !!activeSchoolId && (role === "owner" || role === "admin") },
    { to: "/happyschool/user-accounts", label: "Tài khoản người dùng", icon: "👤", description: "Danh sách tài khoản đăng nhập SĐT: nạp sẵn và người tự đăng ký.", show: !!activeSchoolId && (role === "owner" || role === "admin") },
  ];

  return (
    <div style={containerStyle}>
      <style>{`
        .hs-menu-card { display:flex; align-items:center; gap:16px; padding:16px; border:1px solid #e5e7eb;
          border-radius:12px; background:#fff; text-decoration:none; color:inherit; transition:all .2s ease-in-out;
          box-shadow:0 1px 3px rgba(0,0,0,.05); text-align:left; }
        .hs-menu-card:hover { transform:translateY(-2px); box-shadow:0 10px 15px -3px rgba(0,0,0,.05),0 4px 6px -2px rgba(0,0,0,.05);
          border-color:#0ea5a3; background:#f0fdfa; }
        .hs-menu-icon { width:60px; height:60px; border-radius:12px; display:flex; align-items:center; justify-content:center;
          background:#f3f4f6; font-size:28px; flex-shrink:0; }
        .hs-menu-text { text-align:left; min-width:0; }
        .hs-menu-title { margin:0 0 4px 0; font-size:16px; font-weight:600; color:#1f2937; text-align:left; }
        .hs-menu-desc { margin:0; font-size:13px; color:#4b5563; line-height:1.5; text-align:left; }
        .hs-qr-box { display:flex; gap:12px; flex-shrink:0; }
        .hs-qr-item { text-align:center; width:104px; }
        .hs-qr-item img { width:104px; height:104px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; display:block; }
        .hs-qr-item span { display:block; margin-top:4px; font-size:12px; font-weight:600; color:#374151; }
        @media (max-width:640px){ .hs-menu-card{ flex-direction:column; align-items:flex-start; gap:12px; } .hs-qr-item, .hs-qr-item img { width:84px; } .hs-qr-item img { height:84px; } }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={titleStyle}>HappySchool</h2>
          <p style={subtitleStyle}>Cùng chung tay xây dựng học đường hạnh phúc</p>
        </div>
        <div className="hs-qr-box">
          <div className="hs-qr-item">
            <img src="/qr_android.jpg" alt="QR tải ứng dụng Android" />
            <span>📱 Tải app Android</span>
          </div>
          <div className="hs-qr-item">
            <img src="/qr_web.jpg" alt="QR truy cập web" />
            <span>🌐 Truy cập web</span>
          </div>
        </div>
      </div>

      {!authChecked ? (
        <p style={{ color: "#6b7280" }}>Đang tải...</p>
      ) : !uid ? (
        <p style={{ color: "#6b7280" }}>
          Vui lòng <Link to="/login">đăng nhập</Link> để dùng HappySchool.
        </p>
      ) : loading ? (
        <p style={{ color: "#6b7280" }}>Đang tải danh sách trường...</p>
      ) : schools.length === 0 ? (
        // Đăng nhập lần đầu / chưa thuộc trường nào -> màn hình tham gia trường.
        <HappySchoolJoin accountName={displayName} onJoined={loadSchools} />
      ) : role === "student" ? (
        // Học sinh -> cổng học sinh (điểm, lớp, TKB).
        <HappySchoolStudentPortal schoolId={activeSchoolId} />
      ) : (role === "teacher" || role === "staff") ? (
        // Giáo viên / Cán bộ nhân viên -> cổng riêng (nội quy, xác thực, đổi MK,
        // hồ sơ; GV thêm khai báo lịch bận). Không thấy menu quản trị toàn trường.
        <HappySchoolStaffPortal schoolId={activeSchoolId} isTeacher={role === "teacher"} displayName={displayName} />
      ) : role === "schedule_viewer" ? (
        // Ban giám hiệu (Xếp TKB) -> hiện đúng 1 thẻ menu "Xếp thời khoá
        // biểu" GIỐNG hệt kiểu thẻ bên màn hình owner/admin (bấm vào mới
        // sang trang có cây xếp lịch, không dội thẳng cái cây ra đây), cộng
        // cổng giáo viên bình thường bên dưới (hồ sơ, chấm công, lương, lịch
        // bận...) vì đây vẫn là GV/nhân sự được cấp thêm quyền xếp lịch,
        // không phải vai trò quản trị riêng.
        <>
          {/* Căn cùng maxWidth:900 + margin:auto với HappySchoolStaffPortal
              bên dưới — để lệch nhau (grid rộng 1200 vs portal rộng 900) là
              nguyên nhân giao diện bị thụt thò lúc trước. */}
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Link to="/happyschool/generate" className="hs-menu-card" style={{ marginBottom: 24 }}>
              <div className="hs-menu-icon">🗓️</div>
              <div className="hs-menu-text">
                <h3 className="hs-menu-title">Xếp thời khoá biểu</h3>
                <p className="hs-menu-desc">Phân công chuyên môn, phân bổ văn hoá-nghề, giờ học, lịch bận GV, và chạy xếp lịch.</p>
              </div>
            </Link>
          </div>
          <HappySchoolStaffPortal schoolId={activeSchoolId} isTeacher displayName={displayName} />
          {roles.includes("cntt") && <div style={{ maxWidth: 900, margin: "24px auto 0" }}><HappySchoolTeachingHub /></div>}
        </>
      ) : role === "cntt" ? (
        // CNTT -> CHỈ thấy đúng "Quản lý dạy học" (Giáo viên, Môn học, Lớp
        // học, PCCM, Số tiết, Tổ hợp bộ môn), không thấy các trang quản trị
        // khác — thêm link riêng "Lịch bận của tôi" vì role này không đi qua
        // HappySchoolStaffPortal (chỉ role teacher/staff mới có cổng đó).
        <>
          <p style={{ marginBottom: 16 }}>
            <Link to="/happyschool/my-busy" style={{ fontSize: 14, color: "#0f766e", fontWeight: 600 }}>📅 Lịch bận của tôi</Link>
          </p>
          <HappySchoolTeachingHub />
          {alsoTeacher && <div style={{ marginTop: 24 }}><HappySchoolStaffPortal schoolId={activeSchoolId} isTeacher={roles.some((r) => r === "teacher" || r === "schedule_viewer")} displayName={displayName} /></div>}
        </>
      ) : (role === "owner" || role === "admin") ? (
        <>
          {error && <p style={{ color: "#dc2626" }}>{error}</p>}
          {activeSchool && (
            <p style={boxStyle}>
              Trường đang làm việc: <strong>{activeSchool.name}</strong> ({roles.join(" + ") || activeSchool.role})
            </p>
          )}
          <div style={gridStyle}>
            {menuItems
              .filter((m) => m.show)
              .map((item) => (
                <Link key={item.to} to={item.to} className="hs-menu-card">
                  <div className="hs-menu-icon">{item.icon}</div>
                  <div className="hs-menu-text">
                    <h3 className="hs-menu-title">{item.label}</h3>
                    <p className="hs-menu-desc">{item.description}</p>
                  </div>
                </Link>
              ))}
          </div>
          {alsoTeacher && (
            <div style={{ marginTop: 28 }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 18 }}>🧑‍🏫 Cổng giáo viên của tôi</h3>
              <HappySchoolStaffPortal schoolId={activeSchoolId} isTeacher={roles.some((r) => r === "teacher" || r === "schedule_viewer")} displayName={displayName} />
            </div>
          )}
        </>
      ) : (
        // Vai trò CHƯA XÁC ĐỊNH (vd đang đồng bộ lại activeSchoolId sau khi mở
        // lại app) -> KHÔNG được rơi vào menu quản trị mặc định (fail-open).
        // Luôn ưu tiên trạng thái AN TOÀN NHẤT: chờ, không hiện menu gì.
        <p style={{ color: "#6b7280" }}>Đang xác định quyền truy cập...</p>
      )}
    </div>
  );
};

const containerStyle = { padding: 24, width: "100%", boxSizing: "border-box", maxWidth: 1200, margin: "0 auto" };
const titleStyle = { margin: "0 0 8px 0", fontSize: 24, fontWeight: 700, color: "#111827" };
const subtitleStyle = { margin: "0 0 16px 0", color: "#333", fontSize: 16 };
const boxStyle = { margin: "0 0 20px 0", padding: 12, background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, color: "#134e4a" };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 };

export default HappySchoolHome;
